/*
 * BMP size-aware swatches (Zero Limit Wear, 2026-10-22).
 *
 * Keeps every colour swatch in step with the CURRENTLY checked size:
 *   - size checked    -> a colour is available only if that size+colour variant
 *                        exists and is available (data-bmp-sizes contains it)
 *   - no size checked -> a colour is available if it is available in ANY size
 * A struck-through colour cannot be picked while a size is checked, and if a
 * size switch lands on a combination that does not exist, Add to cart is
 * disabled and labelled Unavailable until the shopper picks a valid colour.
 * Also drops a stale ?bmp_color= once the shopper has chosen another colour,
 * so a reload or shared link keeps the colour they actually picked.
 *
 * Data comes from snippets/variant-main-picker.liquid (data-bmp-sizes on each
 * non-size input, data-bmp-size-fieldset on the gated size fieldset).
 * Server re-renders (morph) overwrite these tweaks, so they are re-applied
 * after every product select settles.
 */
(() => {
  const PICKER = 'variant-picker';
  const SIZE_FIELDSET = '[data-bmp-size-fieldset]';
  const VALUE_INPUT = 'input[type="radio"][data-bmp-sizes]';
  const STRIKE_SVG =
    '<svg viewBox="0 0 100 46" preserveAspectRatio="xMidYMid slice" class="variant-option__strikethrough" aria-hidden="true">' +
    '<line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke" />' +
    '<line x1="100" y1="0" x2="0" y2="46" vector-effect="non-scaling-stroke" /></svg>';

  const checkedSize = (picker) => {
    const fieldset = picker.querySelector(SIZE_FIELDSET);
    if (!fieldset) return undefined; // not a gated picker
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : null;
  };

  const sizesOf = (input) => (input.dataset.bmpSizes ? input.dataset.bmpSizes.split('|') : []);

  const isAvailableFor = (input, size) => {
    const sizes = sizesOf(input);
    return size === null ? sizes.length > 0 : sizes.includes(size);
  };

  const setAvailability = (picker, input, available) => {
    input.dataset.optionAvailable = String(available);
    const soldOut = picker.dataset.bmpSoldOutLabel || 'Sold out';
    if (available) {
      input.removeAttribute('aria-disabled');
      input.setAttribute('aria-label', input.value);
    } else {
      input.setAttribute('aria-disabled', 'true');
      input.setAttribute('aria-label', `${input.value} - ${soldOut}`);
    }
    const label = input.closest('label');
    if (!label) return;
    const strike = label.querySelector('.variant-option__strikethrough');
    if (available && strike) strike.remove();
    if (!available && !strike) label.insertAdjacentHTML('beforeend', STRIKE_SVG);
    if (!label.classList.contains('variant-option__button-label--has-swatch')) {
      const pill = label.querySelector('.variant-option__button-label__pill');
      if (!available && pill) pill.remove();
      if (available && !pill) {
        const span = document.createElement('span');
        span.className = 'variant-option__button-label__pill';
        span.dataset.key = 'variant-option-pill';
        label.insertBefore(span, label.querySelector('.variant-option__button-label__text'));
      }
    }
  };

  // A checked size + checked colour that has no available variant must not be buyable.
  const guardAddToCart = (picker, size) => {
    if (size === null || size === undefined) return;
    const checkedValue = picker.querySelector(`${VALUE_INPUT}:checked`);
    if (!(checkedValue instanceof HTMLInputElement)) return;
    if (isAvailableFor(checkedValue, size)) return;
    const scope = picker.closest('.shopify-section, dialog') || document;
    const label = picker.dataset.bmpUnavailableLabel || 'Unavailable';
    scope.querySelectorAll('product-form-component [ref="addToCartButton"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.disabled = true;
      for (const node of button.childNodes) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          node.textContent = label;
          break;
        }
      }
    });
    const variantId = scope.querySelector('product-form-component input[name="id"]');
    if (variantId instanceof HTMLInputElement) variantId.value = '';
  };

  const dropStaleColorParam = (picker) => {
    if (picker.dataset.templateProductMatch !== 'true') return;
    const url = new URL(window.location.href);
    const wanted = url.searchParams.get('bmp_color');
    if (wanted === null) return;
    const checked = picker.querySelector(`${VALUE_INPUT}:checked`);
    if (checked instanceof HTMLInputElement && checked.value !== wanted) {
      url.searchParams.delete('bmp_color');
      history.replaceState(history.state, '', url.toString());
    }
  };

  const lastChecked = new WeakMap();

  const apply = (picker, { settled = false } = {}) => {
    const size = checkedSize(picker);
    if (size === undefined) return;
    const inputs = picker.querySelectorAll(VALUE_INPUT);
    if (!inputs.length) return;
    const current = picker.querySelector(`${VALUE_INPUT}:checked`);
    if (current) lastChecked.set(picker, current);
    for (const input of inputs) {
      if (input instanceof HTMLInputElement) setAvailability(picker, input, isAvailableFor(input, size));
    }
    if (settled) {
      guardAddToCart(picker, size);
      dropStaleColorParam(picker);
    }
  };

  const applyAll = (options) => document.querySelectorAll(PICKER).forEach((p) => apply(p, options));

  // Block picking a struck-through colour while a size is checked.
  document.addEventListener(
    'click',
    (event) => {
      const input = event.target instanceof Element ? event.target.closest(VALUE_INPUT) : null;
      if (!(input instanceof HTMLInputElement)) return;
      const picker = input.closest(PICKER);
      if (!picker) return;
      const size = checkedSize(picker);
      if (size === undefined || size === null) return;
      if (!isAvailableFor(input, size)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        // Not every engine re-checks the previous radio after a cancelled click.
        const previous = lastChecked.get(picker);
        setTimeout(() => {
          if (!picker.querySelector(`${VALUE_INPUT}:checked`) && previous && previous.isConnected) previous.checked = true;
        }, 0);
      }
    },
    { capture: true }
  );

  // Instant feedback when the size changes, before the section re-render returns.
  document.addEventListener('change', (event) => {
    const picker = event.target instanceof Element ? event.target.closest(PICKER) : null;
    if (picker) apply(picker);
  });

  // Re-apply once the re-render, the product form and bmp-size-required.js have all finished.
  document.addEventListener('shopify:product:select', (event) => {
    const settle = () => setTimeout(() => applyAll({ settled: true }), 0);
    if (event.promise && typeof event.promise.then === 'function') event.promise.then(settle, settle);
    else settle();
  });

  const init = () => applyAll({ settled: false });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  // bmp-size-required.js de-paints sizes on a short schedule; re-sync after it.
  [600, 1500, 3000].forEach((ms) => setTimeout(init, ms));
})();

// BMP-HOOK-START:swatch-fallback-variant (2026-10-24)
// Colour click with NO size chosen, on a colour that has no variant in the
// hidden default size (XS): Horizon would request option_values=<colour> only,
// Shopify resolves that against XS, finds nothing, and the photo + ?variant=
// never change (812 of 868 products: Military Green, Cherry Red, Metro Blue,
// Sage, Heather Maroon, Graphite Heather...).
// Fix: in the capture phase (before variant-picker's own change listener) give
// the clicked input the colour's first available variant id (Liquid:
// data-bmp-fallback-variant-id) and silently check that size so the request
// carries a real colour+size pair. The size is un-checked again before
// bmp-size-required.js snapshots the selection (productSelect, capture), so
// after the morph its empty-snapshot pass leaves the size row unselected and
// the gate stays shut. No change event is fired, so nothing reads as intent.
(() => {
  if (window.__bmpSwatchFallback) return;
  window.__bmpSwatchFallback = true;
  const pending = new WeakMap();
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.dataset.bmpFallbackVariantId) return;
      const picker = input.closest('variant-picker');
      const fieldset = picker && picker.querySelector('[data-bmp-size-fieldset]');
      if (!fieldset || fieldset.querySelector('input[type="radio"]:checked')) return;
      const size = input.dataset.bmpFallbackSize;
      const sizeInput = [...fieldset.querySelectorAll('input[type="radio"]')].find((i) => i.value === size);
      if (!(sizeInput instanceof HTMLInputElement)) return;
      input.dataset.variantId = input.dataset.bmpFallbackVariantId;
      sizeInput.checked = true;
      pending.set(picker, sizeInput);
      // Safety net: never leave the borrowed size checked.
      setTimeout(() => { if (pending.get(picker) === sizeInput) { sizeInput.checked = false; pending.delete(picker); } }, 0);
    },
    { capture: true }
  );
  // Runs before bmp-size-required.js (document, bubble) takes its snapshot.
  document.addEventListener(
    'shopify:product:select',
    (event) => {
      const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
      const sizeInput = picker && pending.get(picker);
      if (!sizeInput) return;
      sizeInput.checked = false;
      pending.delete(picker);
    },
    { capture: true }
  );
  // The section render prints the RESOLVED size in the legend ("Size: XS" /
  // "Size: S") even though nobody chose it (pre-existing on live). Once the
  // morph has settled and bmp-size-required.js has re-emptied the row, blank
  // the legend value of any gated size fieldset with nothing checked.
  const blankLegend = () =>
    document.querySelectorAll('[data-bmp-size-fieldset]').forEach((fs) => {
      if (fs.querySelector('input[type="radio"]:checked')) return;
      const v = fs.querySelector('legend .variant-option__swatch-value');
      if (v && v.textContent.trim()) v.textContent = '';
    });
  document.addEventListener('shopify:product:select', (event) => {
    const done = () => setTimeout(blankLegend, 0);
    if (event.promise && typeof event.promise.then === 'function') event.promise.then(done, done);
    else done();
  });
})();
// BMP-HOOK-END:swatch-fallback-variant
